import { useEffect, useState } from 'react';

import { Partners } from 'constants/partners';
import { useQueryParams } from 'hooks/query-params';
import { Tab, Tabs } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { Candidate } from 'views/Check/tabs/Candidate/Candidate';

import { HistoryRow } from 'components/HistoryRow/HistoryRow';
import { Loading } from 'components/Loading/Loading';

import { billingEntityOptionSetFromStructure } from 'api/billing-api';
import { useSendCandidateRequestEmailMutation } from 'api/candidates-api';
import {
  useAddCandidateChecksMutation,
  useCancelCandidateCheckMutation,
  useGetBillingStructureQuery,
  useGetCandidateHistoryQuery,
  useGetCandidateQuery,
  useGetCompanyChecksQuery,
  useGetCompanyQuery,
  useGetFormsQuery,
  useGetPartnersConfigQuery,
  useGetUsersQuery,
  useLazyGetCandidateCheckResultQuery,
  useLazyGetSubCheckResultQuery,
  useUpdateCandidateCheckMutation,
  useUpdateCandidateMutation,
  useUpdateSubCheckMutation,
} from 'api/partner-v1-api';

import { AssessmentChecksDetails } from '../component/AssessmentChecksDetails/AssessmentChecksDetails';
import styles from './AssessmentProgress.module.scss';

export const AssessmentProgress = () => {
  const { partner, token } = useParams();

  const [loading, setLoading] = useState(false);
  const [applicationId, setApplicationId] = useState();
  const [candidate, setCandidate] = useState({});

  const [bullhornCandidateId, partnerApplicationId, jobAdderApplicationId] = useQueryParams([
    'EntityID',
    'partner_application_id',
    'applicationId',
  ]);

  const {
    data: candidateData,
    isFetching: loadingCandidate,
    refetch,
  } = useGetCandidateQuery(
    { token, applicationId },
    { skip: !applicationId, refetchOnMountOrArgChange: true },
  );
  const { data: entries, isLoading: loadingHistory } = useGetCandidateHistoryQuery(
    { token, applicationId },
    { skip: !applicationId },
  );
  const { data: forms, isLoading: loadingForms } = useGetFormsQuery(token, {
    skip: !applicationId,
  });
  const { data: companyChecks, isLoading: loadingCompanyChecks } = useGetCompanyChecksQuery(token, {
    skip: !applicationId,
  });
  const { data: company, isLoading: loadingCopmpany } = useGetCompanyQuery(token, {
    skip: !applicationId,
  });
  const { data: billingStructure } = useGetBillingStructureQuery(token, {
    skip: !applicationId,
  });
  const billingEntityOptionSet = billingEntityOptionSetFromStructure(billingStructure);

  const { data: partnersConfig, isLoading: loadingPartnersConfig } = useGetPartnersConfigQuery(
    token,
    {
      skip: !applicationId,
    },
  );

  const [getCandidateCheckResult] = useLazyGetCandidateCheckResultQuery();
  const [addCandidateChecks] = useAddCandidateChecksMutation();
  const [updateCandidateCheck] = useUpdateCandidateCheckMutation();
  const [cancelCandidateCheck] = useCancelCandidateCheckMutation();
  const [getSubCheckResult] = useLazyGetSubCheckResultQuery();
  const [sendCandidateRequestEmail] = useSendCandidateRequestEmailMutation();
  const [updateCandidateData] = useUpdateCandidateMutation();
  const [updateSubCheck] = useUpdateSubCheckMutation();

  // Get applicationId based on partner
  useEffect(() => {
    switch (parseInt(partner)) {
      case Partners.Bullhorn:
        setApplicationId(bullhornCandidateId);
        break;
      case Partners.JobAdder:
        setApplicationId(jobAdderApplicationId);
        break;
      default:
        setApplicationId(partnerApplicationId);
    }
  }, [partner, bullhornCandidateId, partnerApplicationId, jobAdderApplicationId]);

  useEffect(() => {
    setCandidate(candidateData);
  }, [candidateData]);

  const shouldSkipUsers = !token || !applicationId;

  const {
    data: users,
    isLoading: loadingUsers,
    error: usersError,
  } = useGetUsersQuery(token, {
    skip: shouldSkipUsers,
  });

  const handleUpdateCandidate = async candidateData => {
    setLoading(true);
    await updateCandidateData({
      token,
      applicationId: candidateData.partner_application_id,
      candidateData,
    });
    setCandidate(candidateData);
    setLoading(false);
  };

  const handleUpdateSubCheck = async (subCheckId, data) => {
    setLoading(true);
    await updateSubCheck({ token, applicationId, subCheckId, data });
    setLoading(false);
  };

  const handleSendCandidateRequestEmail = async () => {
    setLoading(true);
    await sendCandidateRequestEmail(candidate.id);
    onMessagePopUp('New candidate request Sent', MESSAGE_STATE_SUCCESS);
    setLoading(false);
  };

  if (
    !candidate ||
    loading ||
    loadingCopmpany ||
    loadingForms ||
    loadingCompanyChecks ||
    loadingCandidate ||
    loadingHistory ||
    loadingUsers ||
    loadingPartnersConfig
  )
    return <Loading active={true} />;

  return (
    <div className={styles.root}>
      <Tabs id="accountTabs" defaultActiveKey="checks" className="tabs" mountOnEnter={true}>
        <Tab eventKey="history" title="History">
          <div className="flex flex-col gap-4">
            {entries?.map(entry => (
              <HistoryRow
                key={entry.id}
                entry={entry}
                company={company}
                companyChecks={companyChecks}
                candidate={candidate}
                disableLinks
                referees={entry.referee ? [entry.referee] : []}
              />
            ))}
          </div>
        </Tab>
        <Tab eventKey="candidate" title="Candidate">
          <Candidate
            company={company}
            candidate={candidate}
            companyUsers={users}
            customFields={candidate.customFields}
            handleCandidateUpdate={handleUpdateCandidate}
            sendRequestEmail={handleSendCandidateRequestEmail}
            brand={candidate.brand}
            hideBrand
            billingEntityOptionSet={billingEntityOptionSet}
          />
        </Tab>
        <Tab eventKey="checks" title="Background Checks" className={styles.results}>
          <AssessmentChecksDetails
            candidate={candidate}
            company={company}
            partnersConfig={partnersConfig}
            token={token}
            applicationId={applicationId}
            companyChecks={companyChecks}
            forms={forms}
            reload={refetch}
            setLoading={setLoading}
            setCandidate={setCandidate}
            updateCandidateCheck={updateCandidateCheck}
            getSubCheckResult={getSubCheckResult}
            getCandidateCheckResult={getCandidateCheckResult}
            addCandidateChecks={addCandidateChecks}
            cancelCandidateCheck={cancelCandidateCheck}
            handleUpdateSubCheck={handleUpdateSubCheck}
          />
        </Tab>
      </Tabs>
    </div>
  );
};
